/**
 * The one error the client throws (API.md §1.6).
 *
 * Every non-2xx response from the platform carries the same envelope:
 *
 * ```json
 * { "error": { "code": "RATE_LIMITED", "message": "…", "correlationId": "…" } }
 * ```
 *
 * `message` is written to be shown to the person in front of the screen - it never
 * contains a stack trace, a vendor string or an account-existence hint - so the UI may
 * render `error.message` directly and only needs to special-case a code when it wants
 * to do something other than show the sentence (redirect on `SESSION_EXPIRED`, count
 * down on `RATE_LIMITED`).
 */

/**
 * The documented codes, plus the two the client invents when there is no envelope to
 * read. `(string & {})` keeps the union open: an API that adds a code must not turn
 * into a type error in a bundle that predates it.
 */
export type ApiErrorCode =
  // 400
  | 'VALIDATION_FAILED'
  | 'BAD_REQUEST'
  | 'UNSUPPORTED_MEDIA_TYPE'
  // 401
  | 'UNAUTHENTICATED'
  | 'SESSION_EXPIRED'
  // 403
  | 'FORBIDDEN'
  | 'MFA_REQUIRED'
  | 'MFA_STEP_UP_REQUIRED'
  | 'IMPERSONATION_BLOCKED'
  | 'CSRF_ORIGIN_MISMATCH'
  | 'FEATURE_DISABLED'
  | 'TENANT_SUSPENDED'
  // 404
  | 'NOT_FOUND'
  | 'TENANT_NOT_FOUND'
  // 409 / 410
  | 'CONFLICT'
  | 'DUPLICATE'
  | 'VERSION_CONFLICT'
  | 'LINK_EXPIRED'
  | 'DRAFT_EXPIRED'
  // 429
  | 'RATE_LIMITED'
  // 5xx
  | 'INTERNAL'
  | 'INTEGRATION_ERROR'
  | 'INTEGRATION_UNAVAILABLE'
  | 'INTEGRATION_TIMEOUT'
  | 'NOT_READY'
  | 'MAINTENANCE'
  /** The request never reached the API (offline, DNS, CORS preflight refused). */
  | 'NETWORK_ERROR'
  /** A 2xx or non-2xx body that was not the documented envelope. */
  | 'UNEXPECTED_RESPONSE'
  | (string & {});

/** One zod issue as it appears in `details.issues` of a `VALIDATION_FAILED`. */
export interface ApiValidationIssue {
  path: (string | number)[];
  code: string;
  message: string;
}

export interface ApiErrorInit {
  status: number;
  code: ApiErrorCode;
  message: string;
  correlationId: string | null;
  details?: Record<string, unknown>;
  /** Seconds, from the `Retry-After` header. Present on `429` and on a locked account. */
  retryAfter?: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly correlationId: string | null;
  readonly details: Record<string, unknown> | undefined;
  readonly retryAfter: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.correlationId = init.correlationId;
    this.details = init.details;
    this.retryAfter = init.retryAfter;
  }

  /** `VALIDATION_FAILED` issues, or an empty list for every other code. */
  get issues(): ApiValidationIssue[] {
    const raw = this.details?.['issues'];
    return Array.isArray(raw) ? (raw as ApiValidationIssue[]) : [];
  }

  /** 401 in either spelling - the two mean the same thing to a signed-out visitor. */
  get isUnauthenticated(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'SESSION_EXPIRED';
  }

  /** A second factor is needed before this call will succeed (API.md §1.3). */
  get isStepUpRequired(): boolean {
    return this.code === 'MFA_STEP_UP_REQUIRED';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** `Retry-After: 45` -> 45. HTTP-date form and junk both answer `undefined`. */
export function parseRetryAfter(header: string | null): number | undefined {
  if (header === null || header.trim() === '') return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const at = Date.parse(header);
  if (Number.isNaN(at)) return undefined;
  const delta = Math.ceil((at - new Date().getTime()) / 1000);
  return delta > 0 ? delta : 0;
}

/**
 * Turns a response body into an `ApiError`.
 *
 * A body that is not the envelope - an nginx 502 page, a proxy timeout, Better Auth's
 * own `{ code, message }` should one ever escape the bridge - still produces a usable
 * error rather than a parse failure, because a blank screen is the worst outcome here.
 */
export function errorFromBody(status: number, body: unknown, retryAfter?: number): ApiError {
  const envelope =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as { error?: unknown }).error
      : undefined;

  if (typeof envelope === 'object' && envelope !== null) {
    const shape = envelope as {
      code?: unknown;
      message?: unknown;
      details?: unknown;
      correlationId?: unknown;
    };
    return new ApiError({
      status,
      code: typeof shape.code === 'string' ? shape.code : fallbackCode(status),
      message: typeof shape.message === 'string' ? shape.message : fallbackMessage(status),
      correlationId: typeof shape.correlationId === 'string' ? shape.correlationId : null,
      ...(typeof shape.details === 'object' && shape.details !== null
        ? { details: shape.details as Record<string, unknown> }
        : {}),
      ...(retryAfter === undefined ? {} : { retryAfter }),
    });
  }

  return new ApiError({
    status,
    code: fallbackCode(status),
    message: fallbackMessage(status),
    correlationId: null,
    ...(retryAfter === undefined ? {} : { retryAfter }),
  });
}

function fallbackCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'INTERNAL';
  return 'UNEXPECTED_RESPONSE';
}

/**
 * The sentence shown when the API did not supply one. Deliberately plain and free of
 * detail: what went wrong upstream is in the logs, not on the patient's screen.
 */
function fallbackMessage(status: number): string {
  if (status === 401) return 'Please sign in again.';
  if (status === 429) return 'Too many attempts. Please wait and try again.';
  if (status >= 500) return 'Something went wrong on our side. Please try again.';
  return 'Something went wrong. Please try again.';
}
